import { useEffect, type RefObject } from "react";

/** Within a few pixels of the end counts as the bottom (sub-pixel scroll positions). */
const BOTTOM_SLACK_PX = 4;

/** A scroll this soon after a reader's gesture ends (a wheel, a key, touch momentum) is theirs. */
const GESTURE_WINDOW_MS = 1000;

const atBottom = (view: HTMLElement) =>
  view.scrollHeight - view.scrollTop - view.clientHeight <= BOTTOM_SLACK_PX;

/**
 * Keeps a reader who is at the bottom of `viewport` there when `content` grows by itself: an
 * approval frame sizing to its view after it loads, an image decoding. assistant-ui follows new
 * messages and streamed text, but not a box that grows through its style. Someone who scrolled up
 * is left where they are.
 */
export function useKeepAtBottom(
  viewport: RefObject<HTMLElement>,
  content: RefObject<HTMLElement>
): void {
  useEffect(() => {
    const view = viewport.current;
    const inner = content.current;
    if (!view || !inner) return;
    let following = atBottom(view);
    // The position our own scroll left the view at: its scroll event is ours, not the reader's.
    let ownTop: number | null = null;
    // A reader's gesture: held (a touch, a scrollbar drag) or just done (a wheel, a key, momentum).
    let holding = false;
    let gestureEndedAt = -Infinity;
    const readerScrolling = () => holding || performance.now() - gestureEndedAt < GESTURE_WINDOW_MS;

    const onScroll = () => {
      if (ownTop !== null && view.scrollTop === ownTop) {
        ownTop = null;
        return;
      }
      ownTop = null;
      // Back at the bottom by any means follows again; leaving it counts only from the reader.
      if (atBottom(view)) following = true;
      else if (readerScrolling()) following = false;
    };
    const observer = new ResizeObserver(() => {
      if (!following || atBottom(view)) return;
      view.scrollTo({ top: view.scrollHeight, behavior: "instant" });
      ownTop = view.scrollTop;
    });
    const hold = () => {
      holding = true;
    };
    const release = () => {
      holding = false;
      gestureEndedAt = performance.now();
    };
    const listeners: [EventTarget, string, () => void][] = [
      [view, "pointerdown", hold],
      [view, "touchstart", hold],
      [window, "pointerup", release],
      [window, "pointercancel", release],
      [window, "touchend", release],
      [view, "wheel", release],
      [view, "keydown", release],
      [view, "scroll", onScroll],
    ];
    for (const [target, type, listener] of listeners) {
      target.addEventListener(type, listener, { passive: true });
    }
    observer.observe(inner);
    return () => {
      observer.disconnect();
      for (const [target, type, listener] of listeners) target.removeEventListener(type, listener);
    };
  }, [viewport, content]);
}
